import { isDamageDeleted } from '../features/inspections/utils/damageAnnotationUtils';

export type DamageLike = {
  id?: string;
  viewName?: string;
  imageFileId?: string;
  groupId?: string;
  gropdId?: string;
  imageFileGroupKey?: string;
  imageURI?: string;
  imageUrl?: string;
  imagePath?: string;
  image?: string;
  [key: string]: unknown;
};

export type DamageImageKeySource = {
  id?: string;
  viewName?: string;
  imageFileId?: string;
  imageFileGroupKey?: string;
  imageURI?: string;
  imageUrl?: string;
  imagePath?: string;
  image?: string;
};

export type DamageGroup<T extends DamageLike = DamageLike> = {
  imageFileGroupKey: string;
  imageFileId?: string;
  viewName?: string;
  displaySrc?: string;
  damages: Array<T & { imageFileGroupKey: string }>;
};

export const getDamageImageUrl = (damage: DamageImageKeySource): string | undefined => {
  const imageURI = damage.imageURI || damage.imageUrl || damage.imagePath || damage.image;
  return typeof imageURI === 'string' ? imageURI : undefined;
};

const normalizeViewName = (viewName?: string): string => {
  if (!viewName) return 'unknown-view';
  return viewName === 'SCAN' || viewName === 'INTERIOR' ? 'Mobile' : viewName;
};

export const getDamageImageFileGroupKey = (damage: DamageImageKeySource): string => {
  if (damage.imageFileGroupKey) {
    return String(damage.imageFileGroupKey);
  }

  if (damage.imageFileId) {
    return String(damage.imageFileId);
  }

  const imageUrl = getDamageImageUrl(damage);
  if (imageUrl) {
    return imageUrl;
  }

  return `${normalizeViewName(damage.viewName)}::${damage.id ?? 'unknown-damage'}`;
};

export const normalizeDamage = <T extends DamageLike>(damage: T): T & { imageFileGroupKey: string } => {
  return {
    ...damage,
    imageFileGroupKey: getDamageImageFileGroupKey(damage),
  };
};

export const groupDamagesByImageFileId = <T extends DamageLike>(damages: T[]): Array<DamageGroup<T>> => {
  const groups = new Map<string, DamageGroup<T>>();

  damages.forEach(damage => {
    // Soft-deleted rows can still carry metadata on on-prem; callers should
    // filter first, but skip here too so empty/deleted-only cards never appear.
    if (isDamageDeleted(damage)) {
      return;
    }

    const normalizedDamage = normalizeDamage(damage);
    const imageFileGroupKey = normalizedDamage.imageFileGroupKey;
    const existingGroup = groups.get(imageFileGroupKey);

    if (existingGroup) {
      existingGroup.damages.push(normalizedDamage);
      if (!existingGroup.displaySrc) {
        existingGroup.displaySrc = getDamageImageUrl(normalizedDamage);
      }
      return;
    }

    groups.set(imageFileGroupKey, {
      imageFileGroupKey,
      imageFileId: typeof normalizedDamage.imageFileId === 'string' ? normalizedDamage.imageFileId : undefined,
      viewName: typeof normalizedDamage.viewName === 'string' ? normalizedDamage.viewName : undefined,
      displaySrc: getDamageImageUrl(normalizedDamage),
      damages: [normalizedDamage],
    });
  });

  return Array.from(groups.values());
};
